import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Motorcycle, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CouriersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", phone: "", active: true });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/couriers");
      setRows(data);
    } catch { toast.error("Error al cargar domiciliarios"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Nombre y teléfono son obligatorios");
    setSaving(true);
    try {
      await api.post("/couriers", form);
      toast.success("Domiciliario agregado");
      setForm({ name: "", phone: "", active: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const toggle = async (c, active) => {
    try {
      await api.patch(`/couriers/${c.id}`, { active });
      setRows(rows.map((r) => r.id === c.id ? { ...r, active } : r));
    } catch { toast.error("Error"); }
  };

  const remove = async (c) => {
    if (!confirm(`Eliminar a ${c.name}?`)) return;
    try {
      await api.delete(`/couriers/${c.id}`);
      setRows(rows.filter((r) => r.id !== c.id));
      toast.success("Eliminado");
    } catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-6" data-testid="couriers-panel">
      <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Motorcycle size={16} className="text-[#A1A1AA]" />
          <div className="font-display text-lg font-medium tracking-tight">Agregar domiciliario</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#71717A]">Nombre</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Juan Pérez"
              className="bg-[#0A0A0C] border-[#27272A] mt-1"
              data-testid="courier-name"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#71717A]">Teléfono WhatsApp</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+52 55 1234 5678"
              className="bg-[#0A0A0C] border-[#27272A] mt-1 font-mono-jb"
              data-testid="courier-phone"
            />
          </div>
          <Button onClick={add} disabled={saving} className="bg-[#5E6AD2] hover:bg-[#4B55C3] h-10" data-testid="courier-add">
            <Plus size={14} className="mr-1.5" /> {saving ? "..." : "Agregar"}
          </Button>
        </div>
        <p className="text-[10px] text-[#71717A] mt-3">
          Los domiciliarios pueden usar comandos por WhatsApp: <span className="text-[#A1A1AA]">&ldquo;llevo pedidos #123, #124&rdquo;</span>, <span className="text-[#A1A1AA]">&ldquo;entregado #123&rdquo;</span>, <span className="text-[#A1A1AA]">&ldquo;sin entregar #123&rdquo;</span>.
        </p>
      </div>

      <div className="border border-[#27272A] bg-[#121214] rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-[#27272A] text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
          Registrados · {rows.length}
        </div>
        {loading && <div className="p-5 text-xs text-[#71717A]">Cargando...</div>}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center text-sm text-[#71717A]">Sin domiciliarios. Agrega el primero arriba.</div>
        )}
        {rows.map((c) => (
          <div key={c.id} className="grid grid-cols-[1fr_1fr_auto_auto] gap-3 px-5 py-3 items-center border-b border-[#18181B] hover:bg-[#0E0E10]" data-testid={`courier-row-${c.id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#EF4444] flex items-center justify-center text-xs font-medium shrink-0">
                {c.name[0]?.toUpperCase()}
              </div>
              <span className="font-medium truncate">{c.name}</span>
            </div>
            <div className="text-xs text-[#A1A1AA] font-mono-jb">+{c.phone}</div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] uppercase tracking-wider ${c.active ? "text-[#10B981]" : "text-[#71717A]"}`}>
                {c.active ? "Activo" : "Inactivo"}
              </span>
              <Switch checked={c.active} onCheckedChange={(v) => toggle(c, v)} data-testid={`courier-toggle-${c.id}`} />
            </div>
            <button
              onClick={() => remove(c)}
              className="p-1.5 rounded hover:bg-[#27272A] text-[#71717A] hover:text-[#EF4444]"
              data-testid={`courier-delete-${c.id}`}
            >
              <Trash size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
